import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
  Paper,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";

// Upload & Image styles
const UploadCard = styled(Box)(({ theme }) => ({
  border: "2px dashed #ccc",
  borderRadius: 10,
  height: 160,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  color: "#888",
  cursor: "pointer",
  transition: "0.3s ease",
  "&:hover": {
    backgroundColor: "#f5f5f5",
    borderColor: "#333",
  },
}));

const ImageCard = styled(Paper)(({ theme }) => ({
  position: "relative",
  borderRadius: 10,
  overflow: "hidden",
  height: 160,
  boxShadow: theme.shadows[3],
}));

const ImagePreview = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
});

const DeleteOverlay = styled(Box)({
  position: "absolute",
  top: 0,
  right: 0,
  backgroundColor: "rgba(0,0,0,0.6)",
  padding: 4,
  borderBottomLeftRadius: 8,
  zIndex: 1,
});

const KitchenImages = ({ editMode, kitchenData, updateKitchenData }) => {
  const fileInputRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [showOptions, setShowOptions] = useState(false);

  const images = kitchenData.images || [];

  useEffect(() => {
    if (editMode && kitchenData.images?.length > 0) {
      if (typeof kitchenData.images[0] === "string") {
        updateKitchenData({
          images: kitchenData.images.map((url, index) => ({
            id: index,
            file: null,
            preview: url,
            category: "",
          })),
        });
      }
    }
  }, [editMode, kitchenData.images, updateKitchenData]);

  const handleImageUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const preview = URL.createObjectURL(file);
      const newEntry = {
        id: Date.now(),
        file,
        preview,
        category: "",
      };
      updateKitchenData({ images: [...images, newEntry] });
    }
  };

  const handleRemoveImageEntry = (id) => {
    const toRemove = images.find((entry) => entry.id === id);
    if (toRemove?.preview) URL.revokeObjectURL(toRemove.preview);
    const updated = images.filter((entry) => entry.id !== id);
    updateKitchenData({ images: updated });
  };

  return (
    <Box
      sx={{
        p: isMobile ? 1 : 4,
        background: "#fff",
      }}
    >
      <Typography variant="h6" mb={2}>
        Kitchen Image Gallery
      </Typography>

      <Grid container spacing={2}>
        {/* Upload Box always first */}
        <Grid item xs={12} sm={6} md={4}>
          <UploadCard onClick={handleImageUploadClick}>
            <CloudUploadIcon sx={{ fontSize: 36 }} />
            <Typography variant="body2" mt={1}>
              Upload Image
            </Typography>
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              onChange={handleFileChange}
              hidden
            />
          </UploadCard>
        </Grid>

        {/* Then show uploaded images */}
        {images.map((entry) => (
          <Grid item xs={12} sm={6} md={4} key={entry.id}>
            <ImageCard>
              <DeleteOverlay>
                <IconButton
                  onClick={() => handleRemoveImageEntry(entry.id)}
                  size="small"
                  sx={{ color: "#fff" }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </DeleteOverlay>
              <ImagePreview src={entry.preview} alt={`Image ${entry.id}`} />
            </ImageCard>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default KitchenImages;
